/* eslint-disable @typescript-eslint/no-unused-vars */
import { of as of } from 'rxjs';

import { exhaustMap, catchError, map, share, throttleTime, switchMap, debounceTime, filter, withLatestFrom, tap } from 'rxjs/operators';
import { Instrument } from './../../models/quote/instrument.model';
import { StocksExchangeInvestment } from './../../models/exchange/stocks-exchange-investment.model';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { StocksExchangeWalletAction } from './stocks-exchange-wallet.action';
import { BackendService } from '../../services/backend/backend.service';
import { RoutesTable } from '../../resources/routes-table.model';
import { DefaultResponse } from '../../models/form/DefaultResponse';
import { Store } from '@ngrx/store';
import { AppState } from '../../interfaces/app-state.interface';
import { RealtimeService } from '../../services/realtime/realtime.service';
import { IApiQueryListResponse } from '../../interfaces/api/api-query-list-response/api-query-list-response.interface';
import { BackendToFrontendParserService } from '../../services/parser/backend-to-frontend-parser.service';
import { PosRiskAction } from '../posrisk/posrisk.action';
import { Order } from '../../models/exchange/order.model';
import { CustodyClientOrder } from '../../models/custody/custody-client-order.model';
import { CustodyStructuredProduct } from '../../models/custody/custody-structured-product.model';
import { StocksMarketAction } from '../stocks-market/stocks-market.action';
import { Strategy } from '../../models/exchange/strategy.model';
import { Stress } from '../../models/pos-risk/stress.model';
import { InstrumentLite } from '../../models/quote/instrument-lite.model';
import { UtilitiesService } from '../../services/utilities.service';
import { RealtimeHub } from '../../enum/realtime/realtime-hub.enum';
import { ExchangeOrderStatus } from '../../enum/orders/order-status.enum';
import { StructuredProductsAction } from '../structured-products/structured-products.action';
import { InvestmentTypeEnum } from '../../enum/orders/investment-type.enum';

@Injectable()
export class StocksExchangeWalletEffects {

    /**
     * Busca as ordens de um cliente
     */
    getOrders$ = createEffect(() =>
        this.actions$.pipe(
            ofType(StocksExchangeWalletAction.getOrders),
            withLatestFrom(this.store.select((s) => s.authentication)),
            filter(([action, auth]) => auth.currentUser && auth.currentUser.token),
            debounceTime(300),
            switchMap(([action, auth]) => {
                return this.backendService.get<DefaultResponse>(RoutesTable.exchange.routes.getClientOrders.uri)
                    .pipe(
                        map((result: any) => {
                            if (result.ok) {
                                return { type: StocksExchangeWalletAction.getOrdersSuccess, payload: result.value.filter((i) => i) };
                            }
                            return { type: StocksExchangeWalletAction.getOrdersError, payload: result.value };
                        }),
                        catchError((error) => {
                            return of({ type: StocksExchangeWalletAction.getOrdersError, payload: error });
                        }),
                    );
            }),
            share(),
        )
    );

    /**
     * Busca apenas as ordens válidas de um cliente
     */
    getValidOrders$ = createEffect(() =>
        this.actions$.pipe(
            ofType(StocksExchangeWalletAction.getValidOrders),
            withLatestFrom(this.store.select((s) => s.authentication)),
            filter(([action, auth]) => auth.currentUser && auth.currentUser.token),
            debounceTime(300),
            switchMap(([action, auth]) => {
                return this.backendService.get<DefaultResponse>(RoutesTable.exchange.routes.getClientValidOrders.uri)
                    .pipe(
                        map((result: any) => {
                            if (result.ok) {
                                return { type: StocksExchangeWalletAction.getValidOrdersSuccess, payload: result.value.filter((i) => i) };
                            }
                            return { type: StocksExchangeWalletAction.getValidOrdersError, payload: result.value };
                        }),
                        catchError((error) => {
                            return of({ type: StocksExchangeWalletAction.getValidOrdersError, payload: error });
                        }),
                    );
            }),
            share(),
        )
    );

    validOrdersLoaded$ = createEffect(
        () => this.actions$.pipe(
            ofType(StocksExchangeWalletAction.getValidOrdersSuccess),
            share()
        ),
        { dispatch: false }
    );

    /**
     * Busca uma ordem de um cliente
     */
    getOrder$ = createEffect(() =>
        this.actions$.pipe(
            ofType(StocksExchangeWalletAction.getOrder),
            withLatestFrom(this.store.select((s) => s.authentication)),
            filter(([action, auth]) => auth.currentUser && auth.currentUser.token),
            switchMap(([action, auth]) => {
                return this.backendService
                    .get<DefaultResponse>(RoutesTable.composeUri(RoutesTable.exchange.routes.getClientOrder, action.payload))
                    .pipe(
                        map((result: any) => {
                            if (result.ok) {
                                return { type: StocksExchangeWalletAction.getOrderSuccess, payload: result.value };
                            }
                            return { type: StocksExchangeWalletAction.getOrderError, payload: result.value };
                        }),
                        catchError((error) => {
                            return of({ type: StocksExchangeWalletAction.getOrderError, payload: error });
                        }),
                    );
            }),
            share(),
        )
    );

    /**
     * Cancela uma ordem de um cliente
     */
    closeOrder$ = createEffect(() =>
        this.actions$.pipe(
            ofType(StocksExchangeWalletAction.closeOrder),
            withLatestFrom(this.store.select((s) => s.authentication)),
            filter(([action, auth]) => auth.currentUser && auth.currentUser.token),
            switchMap(([action, auth]) => {
                return this.backendService.post<DefaultResponse>(RoutesTable.exchange.routes.closeOrder.uri, action.payload)
                    .pipe(
                        map((result: any) => {
                            if (result.ok) {
                                return { type: StocksExchangeWalletAction.getOrderSuccess, payload: action.payload.orderId };
                            }
                            return { type: StocksExchangeWalletAction.getOrderError, payload: result.value };
                        }),
                        catchError((error) => {
                            return of({ type: StocksExchangeWalletAction.getOrderError, payload: error });
                        }),
                    );
            }),
            share(),
        )
    );

    /**
     * Busca uma estrategia de um cliente
     */
    getStrategy$ = createEffect(() =>
        this.actions$.pipe(
            ofType(StocksExchangeWalletAction.getStrategy),
            withLatestFrom(this.store.select((s) => s.authentication)),
            filter(([action, auth]) => auth.currentUser && auth.currentUser.token),
            switchMap(([action, auth]) => {
                return this.backendService
                    .get<DefaultResponse>(RoutesTable.composeUri(RoutesTable.exchange.routes.getClientStrategy, action.payload))
                    .pipe(
                        map((result: any) => {
                            if (result.ok) {
                                if (!result.value) {
                                    return { type: StocksExchangeWalletAction.strategyNotFound, payload: false};
                                }

                                return { type: StocksExchangeWalletAction.getStrategySuccess, payload: result.value };
                            }
                            return { type: StocksExchangeWalletAction.getStrategyError, payload: result.value };
                        }),
                        catchError((error) => {
                            return of({ type: StocksExchangeWalletAction.getStrategyError, payload: error });
                        }),
                    );
            }),
            share(),
        )
    );

    /**
     * Cancela uma estratégia de um cliente
     */
    closeStrategy$ = createEffect(() =>
        this.actions$.pipe(
            ofType(StocksExchangeWalletAction.closeStrategy),
            withLatestFrom(this.store.select((s) => s.authentication)),
            filter(([action, auth]) => auth.currentUser && auth.currentUser.token),
            switchMap(([action, auth]) => {
            return this.backendService.post<DefaultResponse>(RoutesTable.exchange.routes.closeStrategy.uri, action.payload)
                .pipe(
                    map((result: any) => {
                        if (result.ok) {
                            return { type: StocksExchangeWalletAction.closeStrategySuccess, payload: action.payload.strategyId };
                        }
                        return { type: StocksExchangeWalletAction.closeStrategyError, payload: result.value };
                    }),
                    catchError((error) => {
                        return of({ type: StocksExchangeWalletAction.closeStrategyError, payload: error });
                    }),
                );
            }),
            share(),
        )
    );

    /**
     * Busca as "estratégias" de um cliente
     */
    getStrategies$ = createEffect(() =>
        this.actions$.pipe(
            ofType(StocksExchangeWalletAction.getStrategies),
            withLatestFrom(this.store.select((s) => s.authentication)),
            filter(([action, auth]) => auth.currentUser && auth.currentUser.token),
            debounceTime(300),
            switchMap((action) => {
                return this.backendService.get<DefaultResponse>(RoutesTable.exchange.routes.getClientStrategies.uri)
                .pipe(
                    map((result: any) => {
                        if (result.ok) return { type: StocksExchangeWalletAction.getStrategiesSuccess, payload: result.value };
                        return { type: StocksExchangeWalletAction.getStrategiesError, payload: result.value };
                    }),
                    catchError((error) => {
                        return of({ type: StocksExchangeWalletAction.getStrategiesError, payload: error });
                    }),
                );
            }),
            share(),
        )
    );

    /**
     * Busca apenas as "estratégias" válidas de um cliente
     */
    getValidStrategies$ = createEffect(() =>
        this.actions$.pipe(
            ofType(StocksExchangeWalletAction.getValidStrategies),
            withLatestFrom(this.store.select((s) => s.authentication)),
            filter(([action, auth]) => auth.currentUser && auth.currentUser.token),
            debounceTime(300),
            exhaustMap((action) => {
                return this.backendService.get<DefaultResponse>(RoutesTable.exchange.routes.getClientValidStrategies.uri)
                .pipe(
                    map((result: any) => {
                        if (result.ok) return { type: StocksExchangeWalletAction.getValidStrategiesSuccess, payload: result.value };
                        return { type: StocksExchangeWalletAction.getValidStrategiesError, payload: result.value };
                    }),
                    catchError((error) => {
                        return of({ type: StocksExchangeWalletAction.getValidStrategiesError, payload: error });
                    }),
                );
            }),
            share(),
        )
    );

    validStrategiesLoaded$ = createEffect(
        () => this.actions$.pipe(
            ofType(StocksExchangeWalletAction.getValidStrategiesSuccess),
            share()
        ),
        { dispatch: false }
    );

    /**
     * Envia uma estratégia de checkout planejado
     */
    sendStrategy$ = createEffect(() =>
        this.actions$.pipe(
            ofType(StocksExchangeWalletAction.sendStrategy),
            withLatestFrom(this.store.select((s) => s.authentication)),
            filter(([action, auth]) => auth.currentUser && auth.currentUser.token),
            switchMap((action) => {
                return this.backendService.get<DefaultResponse>(RoutesTable.exchange.routes.getClientStrategies.uri)
                .pipe(
                    map((result: any) => {
                        if (result.ok) {
                            return { type: StocksExchangeWalletAction.sendStrategySuccess, payload: result.value };
                        }
                        return { type: StocksExchangeWalletAction.sendStrategyError, payload: result.value };
                    }),
                    catchError((error) => {
                        return of({ type: StocksExchangeWalletAction.sendStrategyError, payload: error });
                    }),
                );
            }),
            share(),
        )
    );

    /**
     * Busca todas as datas que tiveram movimentações
     */
    getTradingDates$ = createEffect(() =>
        this.actions$.pipe(
            ofType(StocksExchangeWalletAction.getTradingDates),
            withLatestFrom(this.store.select((s) => s.authentication)),
            filter(([action, auth]) => auth.currentUser && auth.currentUser.token),
            switchMap(([action, auth]) => {
                return this.backendService.get<DefaultResponse>(RoutesTable.exchange.routes.tradingDates.uri)
                .pipe(
                    map((result: any) => {
                        if (result.ok) {
                            return { type: StocksExchangeWalletAction.getTradingDatesSuccess, payload: result.value };
                        }
                        return { type: StocksExchangeWalletAction.getTradingDatesError, payload: result.value };
                    }),
                    catchError((error) => {
                        return of({ type: StocksExchangeWalletAction.getTradingDatesError, payload: error });
                    }),
                );
            }),
            share(),
        )
    );

    /**
     * Nova rota que busca todas as datas que tiveram movimentações, ligeiramente diferente da TradingDates
     */
    getMyHistoricStrategies$ = createEffect(() =>
        this.actions$.pipe(
            ofType(StocksExchangeWalletAction.getMyHistoricStrategies),
            withLatestFrom(this.store.select((s) => s.authentication)),
            filter(([action, auth]) => auth.currentUser && auth.currentUser.token),
            switchMap(([action, auth]) => {
                return this.backendService
                .get<DefaultResponse>(RoutesTable.composeUri(RoutesTable.exchange.routes.getMyHistoricStrategies, action.payload))
                .pipe(
                    map((result: any) => {
                        if (result.ok) {
                            return { type: StocksExchangeWalletAction.getMyHistoricStrategiesSuccess, payload: result.value };
                        }
                        return { type: StocksExchangeWalletAction.getMyHistoricStrategiesError, payload: result.value };
                    }),
                    catchError((error) => {
                        return of({ type: StocksExchangeWalletAction.getMyHistoricStrategiesError, payload: error });
                    }),
                );
            }),
            share(),
        )
    );

    /**
     * Busca todas as ordens com base em um filtro passado no payload
     */
    getOrdersSearch$ = createEffect(() =>
        this.actions$.pipe(
            ofType(StocksExchangeWalletAction.getOrdersSearch),
            withLatestFrom(this.store.select((s) => s.authentication)),
            filter(([action, auth]) => auth.currentUser && auth.currentUser.token),
            switchMap(([action, auth]) => {
                return this.backendService
                    .get<DefaultResponse>(RoutesTable.composeUri(RoutesTable.exchange.routes.getOrdersSearch, action.payload))
                    .pipe(
                        map((result: any) => {
                            if (result.ok) {
                                return { type: StocksExchangeWalletAction.getOrdersSearchSuccess, payload: result.value || [] };
                            }
                            return { type: StocksExchangeWalletAction.getOrdersSearchError, payload: result.value };
                        }),
                        catchError((error) => {
                            return of({ type: StocksExchangeWalletAction.getOrdersSearchError, payload: error });
                        }),
                    );
                }),
                share(),
            )
        );

    /**
     * Busca todas as estratégias com base em um filtro passado no payload
     */
    getStrategySearch$ = createEffect(() =>
        this.actions$.pipe(
            ofType(StocksExchangeWalletAction.getStretegiesSearch),
            withLatestFrom(this.store.select((s) => s.authentication)),
            filter(([action, auth]) => auth.currentUser && auth.currentUser.token),
            switchMap(([action, auth]) => {
                return this.backendService
                    .get<DefaultResponse>(RoutesTable.composeUri(RoutesTable.exchange.routes.getStrategiesSearch, action.payload))
                    .pipe(
                        map((result: any) => {
                            if (result.ok) {
                                return { type: StocksExchangeWalletAction.getStretegiesSearchSuccess, payload: result.value || [] };
                            }
                            return { type: StocksExchangeWalletAction.getStretegiesSearchError, payload: result.value };
                        }),
                        catchError((error) => {
                            return of({ type: StocksExchangeWalletAction.getStretegiesSearchError, payload: error });
                        }),
                    );
            }),
            share(),
        )
    );

    getExhangeUpdates$ = createEffect(() =>
        this.actions$.pipe(
            ofType(StocksExchangeWalletAction.getExchangeUpdates),
            withLatestFrom(
                this.store.select((state) => state.authentication.currentUser),
                this.store.select((state) => state.userProfile.user)
            ),
            filter(([action, authenticatedUserData, user]) => {
                const hasSubscribe = this.realtimeService.registerSubscribersAndCheckIfSubscriptionIsNeeded(
                    RealtimeHub.Exchage
                );
                return (!!user && !!authenticatedUserData) && user && authenticatedUserData.cblc && hasSubscribe;
            }),
            tap(([action, authenticatedUserData, user]) => {
                this.realtimeService
                    .subscribeToExchangeUpdates(authenticatedUserData.cblc)
                    .subscribe((update) => {
                        if (update.StrategyID && update.Legs) {
                            if (this.canSendStrategyStatusUpdate(update)) {
                                this.store.dispatch({
                                    type: StocksExchangeWalletAction.strategyUpdated,
                                    payload: this.utilsService.normalize(update),
                                });
                            }
                        } else if (update.ClOrderID) {
                            this.store.dispatch({
                                type: StocksExchangeWalletAction.orderUpdated,
                                payload: this.utilsService.normalize(update),
                            });
                        } else {
                            this.store.dispatch({
                                type: StocksExchangeWalletAction.custodyUpdated,
                                payload: this.utilsService.normalize(update),
                            });
                        }
                    });
            }),
            share(),
        ),
        { dispatch: false }
    );

    getCustodyUpdates$ = createEffect(() =>
        this.actions$.pipe(
            ofType(StocksExchangeWalletAction.getCustodyUpdates),
            withLatestFrom(
                this.store.select((state) => state.authentication.currentUser),
                this.store.select((state) => state.userProfile.user)
            ),
            filter(([action, authenticatedUserData, user]) => {
                const hasSubscribe = this.realtimeService.registerSubscribersAndCheckIfSubscriptionIsNeeded(
                    RealtimeHub.Custody
                );
                return (!!user && !!authenticatedUserData) && user && authenticatedUserData.cblc && hasSubscribe;
            }),
            tap(([action, authenticatedUserData, user]) => {
                this.realtimeService.subscribeToCustodyUpdates(authenticatedUserData.cblc)
                    .subscribe((update) => {
                        this.store.dispatch({ type: StocksExchangeWalletAction.custodyUpdated, payload: update });
                    });
            }),
            share(),
        ),
        { dispatch: false }
    );

    strategyUpdated$ = createEffect(() =>
        this.actions$.pipe(ofType(StocksExchangeWalletAction.strategyUpdated)),
        { dispatch: false }
    );


    orderUpdated$ = createEffect(() =>
        this.actions$.pipe(ofType(StocksExchangeWalletAction.orderUpdated)),
        { dispatch: false }
    );


    custodyUpdated$ = createEffect(() =>
        this.actions$.pipe(ofType(StocksExchangeWalletAction.custodyUpdated)),
        { dispatch: false }
    );


    /**
     * Carregar a lista de investmentos do usuário
     */
    getInvestments$ = createEffect(() =>
        this.actions$.pipe(
            ofType(StocksExchangeWalletAction.getInvestments),
            withLatestFrom(this.store.select((s) => s.authentication)),
            filter(([action, auth]) => auth.currentUser && auth.currentUser.token),
            debounceTime(300),
            switchMap(([action, auth]) => {
                return this.backendService.get<IApiQueryListResponse<StocksExchangeInvestment>>(
                    RoutesTable.exchange.routes.getInvestments.uri
                ).pipe(
                    map((result: any) => {
                        if (result.ok && !result.hasWarning) {
                            const instruments = this.extractInstruments(result.value);
                            const stresses = this.extractStresses(result.value);
                            const strategies = this.extractStrategies(result.value);
                            const orders = this.extractOrders(result.value);
                            const custodies = this.extractCustodies(result.value);
                            const structuredProducts = this.extractStructuredProducts(result.value);

                            return {
                                type: StocksExchangeWalletAction.getInvestmentsSuccess,
                                payload: {
                                    investments: result.value,
                                    instruments,
                                    stresses,
                                    strategies,
                                    orders,
                                    custodies,
                                    structuredProducts
                                }
                            };
                        }
                        return {
                            type: StocksExchangeWalletAction.getInvestmentsError,
                            payload: result.messages
                        };
                    }),
                    catchError((error) => {
                        return of({
                            type: StocksExchangeWalletAction.getInvestmentsError,
                            payload: error
                        });
                    }),
                );
            }),
            share(),
        )
    );

    /**
     * Effect separado para distribuir os dados de investimentos para diferentes partes do store
     * Isso evita race conditions e permite melhor controle de quando os dados são propagados
     */
    investmentsSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(StocksExchangeWalletAction.getInvestmentsSuccess),
            tap((action: any) => {
                // Dispatch de forma controlada e sequencial
                if (action.payload.instruments) {
                    this.store.dispatch({
                        type: StocksMarketAction.getInstrumentsInfoByArraySuccess,
                        payload: action.payload.instruments,
                    });
                }
                if (action.payload.stresses) {
                    this.store.dispatch({
                        type: PosRiskAction.getStressArraySuccess,
                        payload: action.payload.stresses,
                    });
                }
                if (action.payload.strategies) {
                    this.store.dispatch({
                        type: StocksExchangeWalletAction.getValidStrategiesSuccess,
                        payload: action.payload.strategies,
                    });
                }
                if (action.payload.orders) {
                    this.store.dispatch({
                        type: StocksExchangeWalletAction.getValidOrdersSuccess,
                        payload: action.payload.orders,
                    });
                }
                if (action.payload.custodies) {
                    this.store.dispatch({
                        type: PosRiskAction.getClientCustodiesSuccess,
                        payload: action.payload.custodies,
                    });
                }
                if (action.payload.structuredProducts) {
                    this.store.dispatch({
                        type: StructuredProductsAction.getClientCustodiesSuccess,
                        payload: action.payload.structuredProducts,
                    });
                }
            })
        ),
        { dispatch: false }
    );

    public strategiesWithFinalStatus = new Object();

    constructor(
        private actions$: Actions<StocksExchangeWalletAction>,
        private backendService: BackendService,
        private store: Store<AppState>,
        private realtimeService: RealtimeService,
        private parserService: BackendToFrontendParserService,
        private utilsService: UtilitiesService,
    ) {}

    public isStrategyWithFinalStatus(oldStrategyData) {
        return (
            oldStrategyData.Status == ExchangeOrderStatus.Filled ||
            oldStrategyData.Status == ExchangeOrderStatus.Expired ||
            oldStrategyData.Status == ExchangeOrderStatus.CanceledByEvent ||
            oldStrategyData.Status == ExchangeOrderStatus.Cancelled ||
            oldStrategyData.Status == ExchangeOrderStatus.Rejected ||
            oldStrategyData.Status == ExchangeOrderStatus.Suspendend ||
            oldStrategyData.Status == ExchangeOrderStatus.CanceledByEndDayRoutine ||
            oldStrategyData.Status == ExchangeOrderStatus.CanceledByShutdownStrategy
        );
    }

    public canSendStrategyStatusUpdate(strategyReceived) {
        const wasStrategyReceivedSavedWithFinalStatus = this.strategiesWithFinalStatus[strategyReceived.StrategyID];

        if (!wasStrategyReceivedSavedWithFinalStatus
            && this.isStrategyWithFinalStatus(strategyReceived)) {
            this.saveInStrategiesWithFinalStatus(strategyReceived);
        }

        return !wasStrategyReceivedSavedWithFinalStatus;
    }

    public saveInStrategiesWithFinalStatus(strategyReceived) {
        this.strategiesWithFinalStatus[strategyReceived.StrategyID] = strategyReceived;
    }

    /**
     * Extrai informações do stress do ativo a partir de uma lista de investimentos
     * @param investments lista de investimentos
     */
    private extractStresses(investments: StocksExchangeInvestment[]): Stress[] {
        return investments
            .map((i) => new Stress({...i.instrument.stress, ...{instrument: i.symbol}}));
    }
    /**
     * Realiza o parse entre o objeto que recebemos da nova rota única na api para um que o código espera receber (estratégia)
     * @param investments lista de investimentos
     */
    private extractStrategies(investments: StocksExchangeInvestment[]): Strategy[] {
        return investments
            .filter((i) => i.planned && i.investmentType !== InvestmentTypeEnum.StructuredProducts)
            .map((i) => this.parserService.parseInvestmentToStrategy(i));
    }

    /**
     * Realiza o parse entre o objeto que recebemos da nova rota única na api para um que o código espera receber (ordem)
     * @param investments lista de investimentos
     */
    private extractOrders(investments: StocksExchangeInvestment[]): Order[] {
        return investments
            .filter((i) => i.planned !== true && i.id && i.investmentType !== InvestmentTypeEnum.StructuredProducts)
            .map((order) => this.parserService.parseInvestmentToOrder(order));
    }

    /**
     * Realiza o parse entre o objeto que recebemos da nova rota única na api para um que o código espera receber (custódia)
     * @param investments lista de investimentos
     */
    private extractCustodies(investments: StocksExchangeInvestment[] ): CustodyClientOrder[] {
        return investments
            .filter((i) => i.planned !== true && !i.id && i.investmentType !== InvestmentTypeEnum.StructuredProducts)
            .map((order) => this.parserService.parseInvestmentToCustory(order));
    }

    /**
     * Realiza o parse entre o objeto que recebemos da nova rota única na api para um que o código espera receber (custódia)
     * @param investments lista de investimentos
     */
        private extractStructuredProducts(investments: StocksExchangeInvestment[] ): CustodyStructuredProduct[] {
            return investments
                .filter((i) => i.planned !== true && !i.id && i.investmentType === InvestmentTypeEnum.StructuredProducts)
                .map((order) => this.parserService.parseInvestmentToStructuredCustody(order));
        }

    /**
     * Extrai uma lista de instrumentos da lista de investimentos
     * @param investments lista de investimentos a ser usada
     */
    // private extractInstruments(investments: StocksExchangeInvestment[] ): Instrument[] {
    //     return investments
    //         .map((i) => {
    //             const instrument = new Instrument();
    //             const instrumentLite = new InstrumentLite(i.instrument);
    //             instrument.fromInstrumentLite(instrumentLite);
    //             return instrument;
    //         });
    // }

    private extractInstruments(investments: StocksExchangeInvestment[]): Instrument[] {
    return investments.map((i) => {
        const instrument = new Instrument();
        const instrumentLite = new InstrumentLite(i.instrument);

        instrument.fromInstrumentLite(instrumentLite);

        // Injetando a propriedade que está na raiz (i) para dentro do objeto final
        instrument.blockedQuantity = i.blockedQuantity;

        return instrument;
    });
}
}
